// Aviso de tiempo real del TPV (puro, testeable). Decide qué aviso se ve (la
// pastilla de estado, TpvStatusPill + tpvStatus.js) y cuándo se emiten
// `realtime.lost` / `realtime.restored` al diario.
//
// Solo AVISA: nunca bloquea acciones. Vista del aviso:
//   - null:       todo bien → no se muestra nada.
//   - "down":     lleva ≥ BANNER_AFTER_MS caído (sin red, la vuelta no pudo
//                 ponerse al día o el hub crítico no está Connected) → banner
//                 rojo con el tiempo transcurrido.
//   - "restored": volvió Y la puesta al día terminó → banner verde durante
//                 RESTORED_SHOW_MS.
// Un corte más corto que BANNER_AFTER_MS no avisa ni deja rastro.

/** Caído al menos esto → banner rojo (y `realtime.lost`). */
export const BANNER_AFTER_MS = 5_000;
/** Compatibilidad: umbral del antiguo chip. */
export const STALE_AFTER_MS = BANNER_AFTER_MS;
/** Banner verde «Conectado · datos actualizados». */
export const RESTORED_SHOW_MS = 3_000;
/** Conectado pero sin terminar la puesta al día tras esto → recuperado sin verificar. */
export const CATCH_UP_TIMEOUT_MS = 15_000;
/** Hub `/hub/tpv` Connected sin evento ni beacon durante esto → zombie (ver livenessWatchdog). */
export const ZOMBIE_AFTER_MS = 75_000;

/**
 * Motivo de la caída, o null si el tiempo real está bien.
 * @param {object} p
 * @param {boolean} p.online
 * @param {{status: string}} p.health   estado de tpvResume
 * @param {boolean} p.hubDown           el hub crítico no está Connected
 * @returns {"offline"|"resume"|"hub"|null}
 */
export function downReason({ online, health, hubDown }) {
  if (online === false || health?.status === "offline") return "offline";
  if (health?.status === "reconnecting") return "resume";
  if (hubDown) return "hub";
  return null;
}

/** Estado simple (antiguo chip): "offline" | "reconnecting" | null. */
export function computeStaleStatus({ online, health, disconnectedSince, now }) {
  if (online === false || health?.status === "offline") return "offline";
  if (health?.status === "reconnecting") return "reconnecting";
  if (disconnectedSince != null && now - disconnectedSince >= STALE_AFTER_MS) return "reconnecting";
  return null;
}

/** "8 s" · "1 min 05 s" · "1 h 02 min". */
export function formatElapsed(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const p = (n) => String(n).padStart(2, "0");
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${p(s % 60)} s`;
  return `${Math.floor(m / 60)} h ${p(m % 60)} min`;
}

export const NOTICE_TEXT = {
  realtime: "Sin tiempo real · los cambios de otros dispositivos no se están recibiendo",
  offline: "Sin conexión a internet · los cambios de otros dispositivos no se están recibiendo",
  restored: "Conectado · datos actualizados",
  retry: "Reintentar",
};

/**
 * Máquina del aviso. `update(input, now)` devuelve la vista:
 *   { kind: null } | { kind: "down", variant: "offline"|"realtime", elapsedMs } | { kind: "restored" }
 *
 * input:
 *   - online, health, hubDown   (ver downReason)
 *   - hub                       nombre del hub crítico ("tpv" / "mesahub")
 *   - catchUpAvailable          hay catch-up por secuencia (V2). Sin él (legacy)
 *                               los hooks se resincronizan solos al reconectar:
 *                               se da por recuperado al volver.
 *   - lastCaughtUpAt            fin de la última vuelta que terminó bien (tpvResume)
 *
 * Callbacks, una vez por corte: onLost({ hub, reason }) y
 * onRestored({ hub, downMs, caughtUp }).
 */
export function createRealtimeNotice({ onLost, onRestored } = {}) {
  let downSince = null; // inicio del corte
  let lastDownAt = null; // última vez que se vio caído
  let lost = false; // ya se avisó (banner rojo + realtime.lost)
  let reason = null;
  let hubName = null;
  let upSince = null; // volvió; esperando la puesta al día
  let restoredUntil = null;

  const safe = (fn, arg) => {
    try {
      fn?.(arg);
    } catch {
      /* el aviso nunca rompe la operativa */
    }
  };

  const reset = () => {
    downSince = null;
    lastDownAt = null;
    lost = false;
    reason = null;
    upSince = null;
  };

  const downView = (now) => ({
    kind: "down",
    variant: reason === "offline" ? "offline" : "realtime",
    elapsedMs: now - downSince,
  });

  return {
    update(input = {}, now = Date.now()) {
      const r = downReason(input);
      if (r) {
        if (downSince == null) downSince = now;
        lastDownAt = now;
        upSince = null;
        restoredUntil = null;
        reason = r;
        hubName = input.hub || hubName;
        if (!lost && now - downSince >= BANNER_AFTER_MS) {
          lost = true;
          safe(onLost, { hub: hubName, reason });
        }
        return lost ? downView(now) : { kind: null };
      }

      if (downSince != null) {
        if (!lost) {
          reset(); // corte corto: ni aviso ni rastro
        } else {
          if (upSince == null) upSince = now;
          const caughtUp = !input.catchUpAvailable
            || (input.lastCaughtUpAt != null && input.lastCaughtUpAt >= lastDownAt);
          const timedOut = now - upSince >= CATCH_UP_TIMEOUT_MS;
          // Conectado pero poniéndose al día: el rojo sigue hasta tener datos fiables.
          if (!caughtUp && !timedOut) return downView(now);
          safe(onRestored, { hub: hubName, downMs: Math.max(0, lastDownAt - downSince), caughtUp });
          reset();
          restoredUntil = caughtUp ? now + RESTORED_SHOW_MS : null;
        }
      }

      if (restoredUntil != null && now < restoredUntil) return { kind: "restored" };
      restoredUntil = null;
      return { kind: null };
    },
  };
}
